import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiX,
  FiRefreshCw,
  FiImage
} from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import Tutorial from '../components/Tutorial';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import { bookingAPI, BookingService, getBookingServiceImageURL } from '../services/api';
import './PageTemplate.css';
import '../components/Dashboard.css';
import './Booking.css';

interface BookingPageProps {
  onLogout?: () => void;
  userEmail?: string;
}

const Booking: React.FC<BookingPageProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const { showTutorial, currentTutorialStep, completeTutorial, skipTutorial } = useTutorial();
  const [services, setServices] = useState<BookingService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedService, setSelectedService] = useState<BookingService | null>(null);
  const [requestForm, setRequestForm] = useState({
    preferred_date: '',
    preferred_time: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bookingAPI.listServices();
      setServices(data);
    } catch (err: any) {
      console.error('Error fetching booking services:', err);
      setError(err.message || 'Failed to load booking services');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestBooking = (service: BookingService) => {
    setSelectedService(service);
    setRequestForm({
      preferred_date: '',
      preferred_time: '',
      message: ''
    });
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedService) return;

    try {
      setSubmitting(true);
      await bookingAPI.createRequest({
        service: selectedService.id,
        preferred_date: requestForm.preferred_date || undefined,
        preferred_time: requestForm.preferred_time || undefined,
        message: requestForm.message || undefined
      });

      setShowRequestModal(false);
      setShowSuccessModal(true);
      setSelectedService(null);
      setRequestForm({
        preferred_date: '',
        preferred_time: '',
        message: ''
      });
      // Refresh services to update has_user_request status
      fetchServices();
    } catch (err: any) {
      console.error('Error creating booking request:', err);
      alert(err.message || 'Failed to submit booking request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userEmail}
          organization="NAATI"
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="booking-page">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Book Your Assessment</h1>
              <p className="page-description">Select a service and request a booking time</p>
            </motion.div>

            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
                <button onClick={fetchServices} className="retry-btn">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span> Retry</span>
                </button>
              </motion.div>
            )}

            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                <p>Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="empty-state">
                {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 48 })}
                <p>No booking services available</p>
              </div>
            ) : (
              <div className="services-grid">
                {services.map((service) => (
                  <motion.div
                    key={service.id}
                    className="service-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="service-image">
                      {service.image_garage_key ? (
                        <img src={getBookingServiceImageURL(service.id)} alt={service.title} />
                      ) : (
                        <div className="service-image-placeholder">
                          {React.createElement(FiImage as React.ComponentType<{ size?: number }>, { size: 48 })}
                        </div>
                      )}
                    </div>
                    <div className="service-info">
                      <h3>{service.title}</h3>
                      <p className="service-description">{service.description}</p>
                      {service.has_user_request ? (
                        <div className="request-sent-message">
                          {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                          <span>Your request has already been submitted</span>
                        </div>
                      ) : (
                        <button
                          className="btn-book"
                          onClick={() => handleRequestBooking(service)}
                        >
                          {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 18 })}
                          <span>Request Booking</span>
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Modal */}
      <AnimatePresence>
        {showRequestModal && selectedService && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setShowRequestModal(false)}
          >
            <motion.div
              className="modal-content booking-request-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Request Booking: {selectedService.title}</h2>
                <button
                  className="modal-close"
                  onClick={() => !submitting && setShowRequestModal(false)}
                  disabled={submitting}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>
                    {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 16 })}
                    Preferred Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={requestForm.preferred_date}
                    onChange={(e) => setRequestForm({ ...requestForm, preferred_date: e.target.value })}
                    disabled={submitting}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label>
                    {React.createElement(FiClock as React.ComponentType<{ size?: number }>, { size: 16 })}
                    Preferred Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={requestForm.preferred_time}
                    onChange={(e) => setRequestForm({ ...requestForm, preferred_time: e.target.value })}
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Message (Optional)</label>
                  <textarea
                    value={requestForm.message}
                    onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                    placeholder="Add any additional information..."
                    rows={4}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowRequestModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSubmitRequest}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccessModal(false)}
          >
            <motion.div
              className="modal-content success-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="success-icon">
                {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 64 })}
              </div>
              <h2>Request Submitted Successfully!</h2>
              <p>
                Your booking request has been submitted. We will review your request and send you the result via email shortly.
              </p>
              <button
                className="btn-primary"
                onClick={() => setShowSuccessModal(false)}
              >
                OK
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {showTutorial && currentTutorialStep === 4 && (
        <Tutorial
          onComplete={completeTutorial}
          onSkip={skipTutorial}
          initialStep={4}
        />
      )}
    </div>
  );
};

export default Booking;

